import { useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { aoEstadoLicenca, aoSessaoExpirar, api, definirContexto } from '../api/cliente';
import { ErroApi } from '../api/erros';
import type { Empresa, Filial, Sessao } from './tipos';

interface EstadoSessao {
  sessao: Sessao | null;
  carregando: boolean;
  empresa: Empresa | null;
  filial: Filial | null;
  dataBase: string;
  estadoLicenca: string;
  entrar: (login: string, senha: string) => Promise<Sessao>;
  sair: () => Promise<void>;
  /** Relê `/auth/sessao` (depois de 2FA, troca de senha): devolve a etapa em que a sessão está. */
  recarregar: () => Promise<Sessao | null>;
  trocarContexto: (empresa: string, filial?: string) => void;
  definirDataBase: (data: string) => void;
}

const Contexto = createContext<EstadoSessao | null>(null);

export function useSessao(): EstadoSessao {
  const c = useContext(Contexto);
  if (!c) throw new Error('useSessao fora do ProvedorSessao');
  return c;
}

const CHAVE_CONTEXTO = 'persona.contexto';

export function ProvedorSessao({ children }: { children: ReactNode }) {
  const cliente = useQueryClient();
  const [sessao, setSessao] = useState<Sessao | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [escolha, setEscolha] = useState<{ empresa?: string; filial?: string }>(() => {
    try {
      return JSON.parse(sessionStorage.getItem(CHAVE_CONTEXTO) ?? '{}') as { empresa?: string; filial?: string };
    } catch {
      return {};
    }
  });
  const [dataBase, setDataBase] = useState('');
  const [estadoLicenca, setEstadoLicenca] = useState('liberado');

  const aplicar = useCallback((s: Sessao | null) => {
    setSessao(s?.etapa === 'ok' ? s : null);
    if (s?.dataBase) setDataBase((d) => d || s.dataBase);
  }, []);

  useEffect(() => {
    aoSessaoExpirar(() => setSessao(null));
    aoEstadoLicenca(setEstadoLicenca);
    api.get<Sessao>('/auth/sessao')
      .then(aplicar)
      .catch((e) => {
        if (!(e instanceof ErroApi) || e.status !== 401) console.warn('sessão', e);
        aplicar(null);
      })
      .finally(() => setCarregando(false));
  }, [aplicar]);

  const empresa = useMemo(() => {
    if (!sessao) return null;
    return sessao.empresas.find((e) => e.codigo === escolha.empresa) ?? sessao.empresas[0] ?? null;
  }, [sessao, escolha.empresa]);
  const filial = useMemo(() => {
    if (!empresa) return null;
    return empresa.filiais.find((f) => f.codigo === escolha.filial) ?? empresa.filiais.find((f) => f.padrao) ?? empresa.filiais[0] ?? null;
  }, [empresa, escolha.filial]);

  // Cabeçalhos X-Persona-* sempre refletem o contexto escolhido (07 §2.8).
  definirContexto({ empresa: empresa?.codigo, filial: filial?.codigo, dataBase: dataBase || undefined });

  const valor = useMemo<EstadoSessao>(() => ({
    sessao,
    carregando,
    empresa,
    filial,
    dataBase,
    estadoLicenca,
    entrar: async (login, senha) => {
      const s = await api.post<Sessao>('/auth/entrar', { login, senha });
      aplicar(s);
      return s;
    },
    recarregar: async () => {
      const s = await api.get<Sessao>('/auth/sessao');
      aplicar(s);
      return s;
    },
    sair: async () => {
      try {
        await api.post('/auth/sair');
      } finally {
        cliente.clear();
        setSessao(null);
      }
    },
    trocarContexto: (e, f) => {
      const nova = { empresa: e, filial: f };
      setEscolha(nova);
      try { sessionStorage.setItem(CHAVE_CONTEXTO, JSON.stringify(nova)); } catch { /* sem storage: vale só nesta aba */ }
      // Dados dependem do contexto; o esquema do dicionário é global e fica (07 §2.8).
      cliente.removeQueries({ predicate: (q) => q.queryKey[0] === 'dados' });
    },
    definirDataBase: setDataBase,
  }), [sessao, carregando, empresa, filial, dataBase, estadoLicenca, aplicar, cliente]);

  return <Contexto.Provider value={valor}>{children}</Contexto.Provider>;
}
